import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const features = [
    { icon: '🏆', title: t('qualityAssurance'), desc: t('qualityText') },
    { icon: '⚙️', title: t('advancedTech'), desc: t('techText') },
    { icon: '👨‍🎨', title: t('skilledCraft'), desc: t('craftText') },
    { icon: '🤝', title: t('clientCentric'), desc: t('clientText') },
  ];

  const industries = [
    t('residential'),
    t('hotels'),
    t('corporate'),
    t('malls'),
    t('government'),
    t('religious'),
    t('hospitality')
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center" 
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5052704/pexels-photo-5052704.jpeg)'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-none text-white mb-6 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`}
            data-testid="hero-title"
          >
            {t('heroTagline')}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`text-lg sm:text-xl text-white/90 mb-12 ${
              isRTL ? 'font-arabic' : ''
            }`}
            data-testid="hero-subtitle"
          >
            {t('heroSubtitle')}
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/materials"
              data-testid="hero-explore-btn"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-black text-sm font-medium hover:bg-[#C4A661] hover:text-white transition-all duration-300"
            >
              {t('exploreMaterials')}
              <ArrowRight size={20} className="ml-2" weight="bold" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white text-sm font-medium hover:bg-white hover:text-black transition-all duration-300"
            >
              {t('requestQuote')}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="section-padding bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
                {t('about')}
              </p>
              <h2 className={`text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-6 ${
                isRTL ? 'font-arabic' : 'font-heading'
              }`}>
                {t('aboutTitle')}
              </h2>
              <p className={`text-base leading-relaxed text-gray-700 mb-8 ${
                isRTL ? 'font-arabic' : ''
              }`}>
                {t('aboutDescription')}
              </p>
              <Link
                to="/about"
                data-testid="learn-more-btn"
                className="inline-flex items-center text-sm font-medium text-[#C4A661] hover:text-black transition-colors"
              >
                {isRTL ? 'اقرأ المزيد' : 'Learn More'}
                <ArrowRight size={16} className="ml-2" weight="bold" />
              </Link>
            </div>
            <div className="md:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1690049098415-29f96fca22c0"
                alt="Marble texture"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className={`text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-4 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`}>
              {t('whyChooseUs')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 border border-black/5 hover:border-[#C4A661] transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className={`text-xl font-medium mb-3 ${
                  isRTL ? 'font-arabic' : 'font-heading'
                }`}>
                  {feature.title}
                </h3>
                <p className={`text-sm text-gray-600 leading-relaxed ${
                  isRTL ? 'font-arabic' : ''
                }`}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Teaser */}
      <section className="section-padding bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <img
                src="https://images.pexels.com/photos/33685863/pexels-photo-33685863.jpeg"
                alt="Luxury interior"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="md:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
                {t('industries')}
              </p>
              <h2 className={`text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-8 ${
                isRTL ? 'font-arabic' : 'font-heading'
              }`}>
                {t('industriesTitle')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {industries.map((industry, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle size={20} weight="fill" className="text-[#C4A661] flex-shrink-0" />
                    <span className={`text-base text-gray-700 ${
                      isRTL ? 'font-arabic' : ''
                    }`}>
                      {industry}
                    </span>
                  </div>
                ))}
              </div>
              <Link
                to="/industries"
                data-testid="view-industries-btn"
                className="inline-flex items-center px-6 py-3 bg-black text-white text-sm font-medium hover:bg-[#C4A661] transition-all duration-300"
              >
                {isRTL ? 'عرض جميع القطاعات' : 'View All Industries'}
                <ArrowRight size={16} className="ml-2" weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F0F0F] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className={`text-4xl sm:text-5xl font-light tracking-tight mb-6 ${
            isRTL ? 'font-arabic' : 'font-heading'
          }`}>
            {isRTL ? 'ابدأ مشروعك اليوم' : 'Start Your Project Today'}
          </h2>
          <p className={`text-lg text-gray-300 mb-8 ${
            isRTL ? 'font-arabic' : ''
          }`}>
            {isRTL 
              ? 'دعنا نحول رؤيتك إلى واقع مع حلول الرخام المتميزة لدينا'
              : 'Let us transform your vision into reality with our premium marble solutions'
            }
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="inline-block px-10 py-4 bg-[#C4A661] text-white text-sm font-medium hover:bg-white hover:text-black transition-all duration-300"
          >
            {t('contact')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
